import React, { FC, useEffect, useState } from "react";
import { Cta } from "@xapp/stentor-chat-widget";
import { CtaBubbleContainer } from "../CtaBubble";
import {
    defaultMobileWidgetButtonWidth,
    defaultNonMobileScreenWidth,
    defaultWidgetButtonWidth,
} from "../../config";

import "./ChatButton.scss";

export type ChatButtonBorderStyle = {
    width?: number;
    color?: string;
};

export interface ChatButtonProps {
    readonly onClick: () => void;
    /**
     * Optional class to add to the root <button> tag
     */
    readonly addClass?: string;
    readonly config?: Cta;
    readonly visible: boolean;
    readonly borderStyle?: ChatButtonBorderStyle;
    readonly imageUrl?: string;
}

const ChatButton: FC<ChatButtonProps> = ({
    onClick,
    addClass,
    config,
    visible,
    borderStyle,
    imageUrl,
}: ChatButtonProps) => {
    const [buttonWidth, setButtonWidth] = useState<string>(defaultWidgetButtonWidth);
    const [animate, setAnimate] = useState<boolean>(false);

    const mobileWidth = config?.mobile?.applyAtLessThanWidth || defaultNonMobileScreenWidth;

    // Determines mobile or normal
    const getConfigToApply = () => {
        const screenWidth = window.innerWidth;
        if (screenWidth <= parseInt(mobileWidth, 10) && config?.mobile) {
            return config.mobile;
        } else {
            return config;
        }
    };

    const configToApply = getConfigToApply();

    useEffect(() => {
        let delayTimer: NodeJS.Timeout;
        let timeoutTimer: NodeJS.Timeout;

        // Use animationDelay if provided, otherwise fall back to delay for backwards compatibility
        const animationDelayToUse = configToApply?.animationDelay ?? configToApply?.delay;

        if (animationDelayToUse) {
            // Set a delay for the animation to start
            delayTimer = setTimeout(() => {
                setAnimate(true);

                if (configToApply.animationTimeout) {
                    // Stop the animation after the specified timeout
                    timeoutTimer = setTimeout(() => {
                        setAnimate(false);
                    }, configToApply.animationTimeout);
                }
            }, animationDelayToUse);
        } else if (configToApply?.animationTimeout) {
            // Start animation immediately and set a timeout to stop it
            setAnimate(true);
            timeoutTimer = setTimeout(() => {
                setAnimate(false);
            }, configToApply.animationTimeout);
        } else if (configToApply?.animation) {
            // No delay or timeout, start animation immediately
            setAnimate(true);
        }

        return () => {
            // Clear timers on unmount or when dependencies change
            if (delayTimer) clearTimeout(delayTimer);
            if (timeoutTimer) clearTimeout(timeoutTimer);
        };
    }, [configToApply, setAnimate]);

    const animation = animate ? configToApply?.animation || "wiggle" : "none";

    useEffect(() => {
        const handleResize = () => {
            const screenWidth = window.innerWidth;
            const newButtonWidth =
                screenWidth < parseInt(mobileWidth, 10)
                    ? defaultMobileWidgetButtonWidth
                    : defaultWidgetButtonWidth;
            setButtonWidth(newButtonWidth);
        };

        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [mobileWidth]);

    const maxSvgSize = 22;
    const svgSize = Math.min(maxSvgSize, (+buttonWidth / +defaultWidgetButtonWidth) * maxSvgSize);

    return (
        <button
            aria-label="open chat"
            className={`xapp-chat-button ${addClass || ""}`.trim()}
            onClick={onClick}>
            <div
                id="xapp-widget-button"
                className="xapp-chat-button__btn"
                style={{
                    width: `${buttonWidth}px`,
                    height: `${buttonWidth}px`,
                    borderRadius: `${+buttonWidth / 2}px`,
                    border: borderStyle && borderStyle.width ? "solid" : "none",
                    borderWidth: borderStyle?.width || "0px",
                    borderColor: borderStyle?.color || "transparent",
                    animation: `${animation} 1s infinite`,
                }}>
                {imageUrl ? (
                    // Display image from URL
                    <img src={imageUrl} alt="Chat Icon" />
                ) : (
                    // Fallback to default SVG
                    <svg width={svgSize} height={svgSize} viewBox="0 0 22 22">
                        <path
                            d="M13 22l-4-6H2c-1.11-.043-2-.935-2-2V2C0 .89.89 0 2 0h18c1.11 0 2 .892 2 2v12c0 1.067-.89 1.957-2 2h-3l-4 6zm3-8h4c-.005.3-.01-12 0-12-.01.004-18 .006-18 0 .005.006 0 12 0 12h8l3 5 3-5z"
                            fill="#FFF"
                            fillRule="evenodd"
                        />
                    </svg>
                )}
            </div>
            {configToApply && configToApply.message && (
                <div className="xapp-chat-button__cta">
                    <CtaBubbleContainer
                        timeout={configToApply?.timeout}
                        delay={configToApply?.delay}
                        animate={animate}
                        buttonAnimation={animation}
                        dismissible={configToApply?.dismissible}
                        visible={!visible /** Why is this !visible */}>
                        {configToApply?.message}
                    </CtaBubbleContainer>
                </div>
            )}
        </button>
    );
};

export default ChatButton;
